import { Modal } from '@mantine/core';
import { useKeycloak } from '@react-keycloak/web';
import { parse, ParsedQuery } from 'query-string';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ROUTES } from '../routes/routes';
import Company from '../types/company';
import { useAxios } from '../utils/hooks';
import { ConnectStripeLink } from './ConnectStripeButton';
import Loader from './Loader';
import ModalBody, { ModalHeader } from './ModalBody';

const STRIPE_CLIENT_ID = process.env.REACT_APP_STRIPE_CLIENT_ID;
const STRIPE_CONNECT_REDIRECT = process.env.REACT_APP_STRIPE_CONNECT_REDIRECT;
const STRIPE_SCOPE = 'read_write';
const STRIPE_STATE_VAL = 'eMgypEGqzwLZs34r';
interface AuthUrlParams {
    firstName?: string;
    lastName?: string;
    email?: string;
    domain?: string;
    redirect?: string;
}
export const getStripeAuthUrl = ({ firstName, lastName, email, domain, redirect }: AuthUrlParams) =>
    `https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${STRIPE_CLIENT_ID}&scope=${STRIPE_SCOPE}&state=${STRIPE_STATE_VAL}&redirect_uri=${
        redirect || STRIPE_CONNECT_REDIRECT
    }&stripe_landing=login&stripe_user[email]=${email || ''}&stripe_user[url]=${domain || ''}&stripe_user[first_name]=${
        firstName || ''
    }&stripe_user[last_name]=${lastName || ''}`;

const paramToString = (param: string | string[] | null) =>
    param ? (typeof param === 'string' ? param : param[0]) : '';

interface SuccessAuthResponse {
    code: string;
    scope: string;
}

interface FailureAuthResponse {
    error: string;
    errorDescription?: string;
}

type AuthResponse = SuccessAuthResponse | FailureAuthResponse;

export const getStripeAuthResponse = (query: ParsedQuery<string>): AuthResponse | undefined => {
    const { state, scope, code, error, error_description } = query;

    if (!code) {
        if (error === 'access_denied') {
            return { error: 'You must connect with Stripe in order to use PriceWell.' };
        }
        //not an OAuth response
        return;
    } else if (state !== STRIPE_STATE_VAL) {
        console.error(`state: ${state} doesn't match`);
        return;
    } else if (scope !== STRIPE_SCOPE) {
        console.error(`scope: ${scope} doesn't match`);
        return;
    } else if (error) {
        return { error: paramToString(error), errorDescription: paramToString(error_description) };
    } else {
        return { code: paramToString(code), scope };
    }
};

export const isStripeFailureResponse = (response: AuthResponse): response is FailureAuthResponse => {
    return (response as FailureAuthResponse).error !== undefined;
};

interface Props {
    isOpen: boolean;
    company?: Company;
    onClose: () => void;
}

export default function ConnectStripeModal({ isOpen, company, onClose }: Props) {
    if (company && company?.stripe?.stripeId) {
        return null;
    }

    return (
        <>
            {company && (
                <Modal.Root size="xl" opened={isOpen} onClose={onClose}>
                    <Modal.Overlay />
                    <Modal.Content>
                        <Modal.Header>
                            <Modal.Title>
                                <ModalHeader title="Connect Stripe to get the best experience." type="INFO" />
                            </Modal.Title>
                            <Modal.CloseButton />
                        </Modal.Header>
                        <Modal.Body p={0}>
                            <ModalBody
                                type="INFO"
                                width="HALF"
                                body={<ConnectStripe company={company} />}
                                confirmButtonText="Skip"
                                onConfirm={onClose}
                            />
                        </Modal.Body>
                    </Modal.Content>
                </Modal.Root>
            )}
        </>
    );
}

function ConnectStripe(props: { company: Company }) {
    const { keycloak } = useKeycloak();
    const axios = useAxios();
    const history = useHistory();
    const tokenData = keycloak.tokenParsed as any;
    const authorizeUrl = getStripeAuthUrl({
        firstName: tokenData?.given_name || '',
        lastName: tokenData?.family_name || '',
        email: tokenData?.email || '',
        domain: props.company?.domain || '',
    });
    const { search } = useLocation();
    const [error, setError] = useState<string>();
    const [stripeConnected, setStripeConnected] = useState(false);

    const query = parse(search);
    const authResponse = getStripeAuthResponse(query);

    useEffect(() => {
        if (stripeConnected) {
            return;
        }

        if (authResponse && isStripeFailureResponse(authResponse)) {
            setError(authResponse.errorDescription || authResponse.error);
        }

        if (authResponse && !isStripeFailureResponse(authResponse)) {
            setStripeConnected(true);
            axios.current
                ?.post('/stripe/connect', { code: authResponse.code })
                .then(() => {
                    window.location.href = ROUTES.DASHBOARD;
                })
                .catch((err) => {
                    setError(err?.response?.data?.error ?? 'Something went wrong.');
                });
        }
    }, [axios, history, stripeConnected, authResponse, tokenData?.email]);

    if (!!authResponse) {
        return <Loader />;
    }

    return (
        <>
            <div className="my-6 mx-auto">
                <div className="aspect-w-16 aspect-h-9">
                    <iframe
                        loading="lazy"
                        src="https://www.youtube-nocookie.com/embed/U6qB8ovc-vo?modestbranding=1&showinfo=0&fs=0"
                        title="Video explaining how to connect a Stripe.com account with PriceWell"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    ></iframe>
                </div>
            </div>
            <p className="sm:text-l md:text-2xl text-center">Use your real products by connecting Stripe.</p>
            <p className="flex justify-center mt-8 mb-8">
                <img src="/images/arrow.png" alt="" className="h-14" />
            </p>
            <p className="flex flex-wrap flex-col-reverse md:flex-row justify-center">
                <div>
                    <ConnectStripeLink href={authorizeUrl}>Register with</ConnectStripeLink>
                </div>
                <div className="mx-4">or</div>
                <div>
                    <ConnectStripeLink href={authorizeUrl}>Connect with</ConnectStripeLink>
                </div>
            </p>
            {error && (
                <p className="text-red-700 mt-2">
                    {error} <br /> Please try again or contact support.
                </p>
            )}
            <p className="text-sm mt-8">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1792 1792" className="w-4 inline">
                    <path d="M640 768h512V576q0-106-75-181t-181-75-181 75-75 181v192zm832 96v576q0 40-28 68t-68 28H416q-40 0-68-28t-28-68V864q0-40 28-68t68-28h32V576q0-184 132-316t316-132 316 132 132 316v192h32q40 0 68 28t28 68z" />
                </svg>{' '}
                <span className="font-bold">Secure Connection to Stripe</span>. PriceWell does not access or store your
                financial data in any way. <br /> By connecting your Stripe account you agree to our{' '}
                <a href="https://www.pricewell.com/terms" target="_blank" rel="noreferrer" className="link">
                    terms
                </a>{' '}
                and{' '}
                <a href="https://www.pricewell.com/privacy" target="_blank" rel="noreferrer" className="link">
                    privacy policy
                </a>
                .
            </p>
        </>
    );
}
